import {Box} from '@mui/system';
import {createFileRoute, useNavigate} from '@tanstack/react-router';
import {useCallback, useState} from 'react';
import {useTranslation} from 'tolgee';

import {abortAllRequests} from '~api/api';
import {useFeatureFlags} from '~utils/featureFlags';
import {projectIdQueryParam, Route as RouteEnum} from '~utils/routeUtil';
import {useAnalytics, useTrackEvent} from '~hooks/analytics.hooks';
import {Project} from '~redux/types/user';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {imageDataActions} from '~redux/reducers/imageReducer';
import {selectDefaultProjectId, selectLandingPage, selectProjects, selectUser} from '~redux/reducers/userReducer';
import {setCurrentProjectId} from '~redux/actions/userActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {AddContent} from '~components/common/AddContent';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {CreateProjectDialog} from '~components/user/CreateProjectDialog';
import {ProjectDetails} from '~components/user/ProjectDetails';
import {Can} from 'src/contexts/AbilitiesContext';

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
});

function ProjectsPage(): JSX.Element {
  const {t} = useTranslation('user');
  const projects: Project[] = useAppSelector(selectProjects);
  const defaultProjectId = useAppSelector(selectDefaultProjectId);
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const {toggleEnableProjectCreation} = useFeatureFlags();
  const dispatch = useAppDispatch();
  const userLandingPage = useAppSelector(selectLandingPage);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const trackEvent = useTrackEvent();
  const user = useAppSelector(selectUser);

  const byDefaultProjectAndAlphabetically = useCallback(
    (a: Project, b: Project) => {
      if (a.id === defaultProjectId) return -1;
      if (b.id === defaultProjectId) return 1;
      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
      return 0;
    },
    [defaultProjectId],
  );

  const sortedProjects = [...projects].sort(byDefaultProjectAndAlphabetically);

  const handleCloseCreationDialog = () => {
    setIsCreateProjectDialogOpen(false);
  };

  const handleOpenCreationDialog = () => {
    setIsCreateProjectDialogOpen(true);
  };

  const handleProjectSelect = async (projectId: string, newlyCreated?: boolean) => {
    dispatch(setCurrentProjectId(projectId));
    dispatch(annotatorActions.setCurrentModel(undefined));
    await abortAllRequests();
    dispatch(imageDataActions.resetState());
    const route = userLandingPage ? `/${userLandingPage.toLowerCase()}` : RouteEnum.archive;

    if (analytics) {
      const customer = user?.email.endsWith('maddox.ai') ? `${user?.customer}_internal` : user?.customer;
      analytics.setGroup('project', `${customer}_${projectId}`);
      const groupIdentify = new analytics.Identify();
      customer && groupIdentify.set('customer', customer);
      analytics.groupIdentify('project', `${customer}_${projectId}`, groupIdentify);
      trackEvent('project_selected', {projectId});
    }
    if (newlyCreated) {
      await navigate({to: RouteEnum.archive as any, search: {[projectIdQueryParam]: projectId} as any});
    } else {
      await navigate({to: route as any, search: {[projectIdQueryParam]: projectId} as any});
    }
  };

  return (
    <BaseLayout>
      <Box display='flex' paddingBottom={3} gap={4} flexWrap='wrap'>
        {sortedProjects.map((project: Project) => (
          <ProjectDetails key={project.id} project={project} handleProjectSelect={handleProjectSelect} />
        ))}
        {toggleEnableProjectCreation && (
          <Can I='create' a='Project'>
            <AddContent sx={{flexBasis: '350px'}} buttonText={t('createProject')} onClick={handleOpenCreationDialog} />
          </Can>
        )}
        <CreateProjectDialog
          open={isCreateProjectDialogOpen}
          onClose={handleCloseCreationDialog}
          handleProjectSelect={handleProjectSelect}
        />
      </Box>
    </BaseLayout>
  );
}
